import React, { useEffect, useCallback, useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    MenuItem,
    InputAdornment,
    Card,
    Autocomplete,
    CircularProgress,
    Alert,
    Chip
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DescriptionIcon from '@mui/icons-material/Description';
import { useFormContext } from 'react-hook-form';
import { InvoiceDatesSectionProps, InvoiceOption } from '../../../types';
import { useResponsive } from '../../../../../utils/useResponsive';
import { request } from '../../../../../utils/request';

const InvoiceDatesSection: React.FC<InvoiceDatesSectionProps> = ({
    // Labels with defaults
    invoiceNumberLabel = 'Invoice Number',
    invoiceDateLabel = 'Invoice Date',
    paymentTermsLabel = 'Payment Terms',
    dueDateLabel = 'Due Date',

    // Values
    invoiceNumber: propInvoiceNumber,
    invoiceDate: propInvoiceDate,
    paymentTerms: propPaymentTerms,
    dueDate: propDueDate,
    invoiceId: propInvoiceId,
    partyId,
    vendorId,
    gstType,
    // NEW: Flag to determine if it's purchase or sales
    isPurchase = false, // Default to false (sales mode)

    // Form field names with defaults
    formFieldNames = {
        invoiceNumber: 'invoiceNumber',
        invoiceDate: 'invoiceDate',
        paymentTerms: 'paymentTerms',
        dueDate: 'dueDate',
        invoiceId: 'invoiceId'
    },

    // Callbacks
    onInvoiceDateChange,
    onPaymentTermsChange,
    onDueDateChange,

    // Configuration with defaults
    autoGenerateInvoiceNumber = true,
    nextInvoiceNumber,
    showPaymentTerms = true,
    showDueDate = true,
    disabled = false,
    compact = false,
    isInvoiceLink = false,

    // Payment terms options with defaults
    paymentTermsOptions = [
        { value: 'immediate', label: 'Immediate', days: 0 },
        { value: 'net7', label: 'Net 7', days: 7 },
        { value: 'net15', label: 'Net 15', days: 15 },
        { value: 'net30', label: 'Net 30', days: 30 },
        { value: 'net45', label: 'Net 45', days: 45 },
        { value: 'net60', label: 'Net 60', days: 60 },
        { value: 'custom', label: 'Custom' }
    ]
}) => {
    // Responsive hook
    const { isMobile, isTablet, isMobileOrTablet } = useResponsive();

    // Determine compact mode based on screen size if not explicitly set
    const shouldBeCompact = compact || isMobileOrTablet;

    // Determine field widths based on screen size
    const getFieldWidth = () => {
        if (isMobile) return '100%'; // Full width on mobile
        if (isTablet) return '47%';  // Two columns on tablet
        return '48%'; // Two columns on desktop
    };

    // Determine spacing based on screen size
    const getSpacing = () => {
        if (isMobile) return 1.5;
        if (isTablet) return 2;
        return shouldBeCompact ? 1.5 : 2;
    };

    // React Hook Form integration
    const { register, setValue, watch, getValues } = useFormContext?.() || {};

    // State for invoice linking
    const [invoices, setInvoices] = useState<InvoiceOption[]>([]);
    const [loadingInvoices, setLoadingInvoices] = useState(false);
    const [invoiceSearch, setInvoiceSearch] = useState('');
    const [selectedInvoice, setSelectedInvoice] = useState<InvoiceOption | null>(null);

    // Get form values if using react-hook-form
    const formInvoiceNumber = formFieldNames.invoiceNumber ? watch?.(formFieldNames.invoiceNumber) : undefined;
    const formInvoiceDate = formFieldNames.invoiceDate ? watch?.(formFieldNames.invoiceDate) : undefined;
    const formPaymentTerms = formFieldNames.paymentTerms ? watch?.(formFieldNames.paymentTerms) : undefined;
    const formDueDate = formFieldNames.dueDate ? watch?.(formFieldNames.dueDate) : undefined;
    const formInvoiceId = formFieldNames.invoiceId ? watch?.(formFieldNames.invoiceId) : undefined;

    // Use form values if available, otherwise use props
    const displayInvoiceNumber = formInvoiceNumber !== undefined ? formInvoiceNumber : propInvoiceNumber;
    const displayInvoiceDate = formInvoiceDate !== undefined ? formInvoiceDate : propInvoiceDate;
    const displayPaymentTerms = formPaymentTerms !== undefined ? formPaymentTerms : propPaymentTerms;
    const displayDueDate = formDueDate !== undefined ? formDueDate : propDueDate;

    // Determine if we should fetch invoices based on available IDs
    const shouldFetchInvoices = useCallback(() => {
        if (!isInvoiceLink) return false;

        if (isPurchase) {
            // For purchase: need either partyId OR vendorId
            return !!(partyId || vendorId);
        } else {
            // For sales: need partyId
            return !!partyId;
        }
    }, [isInvoiceLink, isPurchase, partyId, vendorId]);

    // Get the appropriate ID for API call
    const getApiParams = useCallback(() => {
        if (isPurchase) {
            // For purchase invoices, pass both partyId and vendorId if available
            const params: any = { gstType };
            if (partyId) params.partyId = partyId;
            if (vendorId) params.vendorId = vendorId;
            return params;
        } else {
            // For sales invoices, only pass partyId
            return {
                partyId,
                gstType
            };
        }
    }, [isPurchase, partyId, vendorId, gstType]);

    // Get the appropriate API endpoint
    const getApiEndpoint = useCallback(() => {
        return isPurchase
            ? 'purchase/get-all-purchase-invoices'
            : 'invoice/get-all-sales-invoices';
    }, [isPurchase]);

    // Fetch invoices for the selected party/vendor
    useEffect(() => {
        const fetchInvoices = async () => {
            if (!shouldFetchInvoices()) {
                setInvoices([]);
                return;
            }

            setLoadingInvoices(true);
            try {
                const params = getApiParams();

                const res = await request({
                    url: getApiEndpoint(),
                    method: 'get',
                    params: params
                });

                if (res.success && res.data) {
                    setInvoices(res.data);

                    // If there's an existing invoiceId in the form, pre-select it
                    if (formInvoiceId && res.data.length > 0) {
                        const existingInvoice = res.data.find((inv: InvoiceOption) => inv._id === formInvoiceId);
                        if (existingInvoice) {
                            setSelectedInvoice(existingInvoice);
                        }
                    }
                }
            } catch (error) {
                console.error('Error fetching invoices:', error);
            } finally {
                setLoadingInvoices(false);
            }
        };

        fetchInvoices();
    }, [partyId, vendorId, gstType, isInvoiceLink, formInvoiceId, shouldFetchInvoices, getApiParams, getApiEndpoint]);

    // Handle invoice selection
    const handleInvoiceSelect = (invoice: InvoiceOption | null) => {
        setSelectedInvoice(invoice);

        // Only set the invoice ID in the form
        if (invoice) {
            if (formFieldNames.invoiceId && setValue) {
                setValue(formFieldNames.invoiceId, invoice._id);
            }
        } else {
            // Clear invoice ID if deselected
            if (formFieldNames.invoiceId && setValue) {
                setValue(formFieldNames.invoiceId, '');
            }
        }
    };

    // Memoized function to calculate due date
    const calculateDueDate = useCallback((paymentTerms: string, invoiceDate: string) => {
        if (!paymentTerms || !invoiceDate || !showDueDate) return null;

        const selectedTerm = paymentTermsOptions.find(term => term.value === paymentTerms);
        if (selectedTerm?.days !== undefined) {
            const invoiceDateObj = new Date(invoiceDate);
            invoiceDateObj.setDate(invoiceDateObj.getDate() + selectedTerm.days);
            return invoiceDateObj.toISOString().split('T')[0];
        }
        return null;
    }, [paymentTermsOptions, showDueDate]);

    // Calculate due date based on payment terms - FIXED VERSION
    useEffect(() => {
        if (!displayPaymentTerms || !displayInvoiceDate || !showDueDate) return;

        const calculatedDueDate = calculateDueDate(displayPaymentTerms, displayInvoiceDate);
        if (!calculatedDueDate) return;

        // Only update if the due date is different from current value
        if (displayDueDate !== calculatedDueDate) {
            // Update due date in form
            if (formFieldNames.dueDate && setValue) {
                setValue(formFieldNames.dueDate, calculatedDueDate);
            }

            // Call callback
            if (onDueDateChange) {
                onDueDateChange(calculatedDueDate);
            }
        }
    }, [
        displayPaymentTerms,
        displayInvoiceDate,
        showDueDate,
        formFieldNames.dueDate,
        setValue,
        onDueDateChange,
        calculateDueDate,
        displayDueDate
    ]);

    // Set default invoice number if auto-generating
    useEffect(() => {
        if (autoGenerateInvoiceNumber && nextInvoiceNumber && formFieldNames.invoiceNumber && setValue) {
            if (!getValues?.(formFieldNames.invoiceNumber)) {
                setValue(formFieldNames.invoiceNumber, nextInvoiceNumber);
            }
        }
    }, [autoGenerateInvoiceNumber, nextInvoiceNumber, formFieldNames.invoiceNumber, setValue, getValues]);

    // Set default date to today
    useEffect(() => {
        if (!displayInvoiceDate && formFieldNames.invoiceDate && setValue) {
            const today = new Date().toISOString().split('T')[0];
            setValue(formFieldNames.invoiceDate, today);
            if (onInvoiceDateChange) {
                onInvoiceDateChange(today);
            }
        }
    }, [displayInvoiceDate, formFieldNames.invoiceDate, onInvoiceDateChange, setValue]);

    const handleDateChange = (date: string) => {
        if (formFieldNames.invoiceDate && setValue) {
            setValue(formFieldNames.invoiceDate, date);
        }
        if (onInvoiceDateChange) {
            onInvoiceDateChange(date);
        }
    };

    const handlePaymentTermsChange = (terms: string) => {
        if (formFieldNames.paymentTerms && setValue) {
            setValue(formFieldNames.paymentTerms, terms);
        }
        if (onPaymentTermsChange) {
            onPaymentTermsChange(terms);
        }
    };

    const handleDueDateChange = (date: string) => {
        if (formFieldNames.dueDate && setValue) {
            setValue(formFieldNames.dueDate, date);
        }
        if (onDueDateChange) {
            onDueDateChange(date);
        }
    };

    // Get the appropriate placeholder text
    const getInvoiceLinkPlaceholder = () => {
        if (isPurchase) {
            return !(partyId || vendorId)
                ? "Select a party or vendor first to see their purchase invoices"
                : "Search purchase invoices...";
        } else {
            return !partyId
                ? "Select a party first to see their invoices"
                : "Search invoices...";
        }
    };

    // Field width based on responsive design
    const fieldWidth = getFieldWidth();
    const spacing = getSpacing();

    return (
        <Card sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: spacing,
            p: shouldBeCompact ? 1.5 : 2,
            borderRadius: 1,
            width: '100%',
            height: '100%'
        }}>
            {/* Invoice Number */}
            <Box sx={{
                width: fieldWidth,
                minWidth: isMobile ? '100%' : 'auto'
            }}>
                <Typography
                    variant={shouldBeCompact ? "body2" : "body1"}
                    fontWeight="medium"
                    sx={{
                        mb: 0.5,
                        fontSize: isMobile ? '0.875rem' : 'inherit'
                    }}
                >
                    {invoiceNumberLabel}
                </Typography>
                <TextField
                    fullWidth
                    size={shouldBeCompact ? "small" : "small"}
                    disabled={true}
                    value={displayInvoiceNumber || nextInvoiceNumber}
                    InputProps={{
                        sx: {
                            bgcolor: 'grey.50',
                            fontSize: isMobile ? '0.875rem' : 'inherit'
                        }
                    }}
                    {...(formFieldNames.invoiceNumber && register?.(formFieldNames.invoiceNumber))}
                />
            </Box>

            {/* Invoice Date */}
            <Box sx={{
                width: fieldWidth,
                minWidth: isMobile ? '100%' : 'auto'
            }}>
                <Typography
                    variant={shouldBeCompact ? "body2" : "body1"}
                    fontWeight="medium"
                    sx={{
                        mb: 0.5,
                        fontSize: isMobile ? '0.875rem' : 'inherit'
                    }}
                >
                    {invoiceDateLabel}
                </Typography>
                <TextField
                    fullWidth
                    type="date"
                    size={shouldBeCompact ? "small" : "small"}
                    disabled={disabled}
                    value={displayInvoiceDate || new Date().toISOString().split('T')[0]}
                    onChange={(e) => handleDateChange(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <CalendarTodayIcon fontSize={shouldBeCompact ? "small" : "small"} />
                            </InputAdornment>
                        ),
                        sx: {
                            fontSize: isMobile ? '0.875rem' : 'inherit'
                        }
                    }}
                    {...(formFieldNames.invoiceDate && register?.(formFieldNames.invoiceDate, {
                        required: 'Invoice date is required'
                    }))}
                />
            </Box>

            {/* Payment Terms (Optional) */}
            {showPaymentTerms && (
                <Box sx={{
                    width: fieldWidth,
                    minWidth: isMobile ? '100%' : 'auto'
                }}>
                    <Typography
                        variant={shouldBeCompact ? "body2" : "body1"}
                        fontWeight="medium"
                        sx={{
                            mb: 0.5,
                            fontSize: isMobile ? '0.875rem' : 'inherit'
                        }}
                    >
                        {paymentTermsLabel}
                        <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                            sx={{ ml: 0.5, fontSize: isMobile ? '0.75rem' : 'inherit' }}
                        >
                            (Optional)
                        </Typography>
                    </Typography>
                    <TextField
                        fullWidth
                        select
                        size={shouldBeCompact ? "small" : "small"}
                        disabled={disabled}
                        value={displayPaymentTerms || ''}
                        onChange={(e) => handlePaymentTermsChange(e.target.value)}
                        InputProps={{
                            sx: {
                                fontSize: isMobile ? '0.875rem' : 'inherit'
                            }
                        }}
                        {...(formFieldNames.paymentTerms && register?.(formFieldNames.paymentTerms))}
                    >
                        <MenuItem value="">
                            <Typography variant="body2" color="text.secondary">
                                Select payment terms
                            </Typography>
                        </MenuItem>
                        {paymentTermsOptions.map((option) => (
                            <MenuItem
                                key={option.value}
                                value={option.value}
                                sx={{
                                    fontSize: isMobile ? '0.875rem' : 'inherit'
                                }}
                            >
                                {option.label}
                            </MenuItem>
                        ))}
                    </TextField>
                </Box>
            )}

            {/* Due Date (Optional) */}
            {showDueDate && (
                <Box sx={{
                    width: fieldWidth,
                    minWidth: isMobile ? '100%' : 'auto'
                }}>
                    <Typography
                        variant={shouldBeCompact ? "body2" : "body1"}
                        fontWeight="medium"
                        sx={{
                            mb: 0.5,
                            fontSize: isMobile ? '0.875rem' : 'inherit'
                        }}
                    >
                        {dueDateLabel}
                        <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                            sx={{ ml: 0.5, fontSize: isMobile ? '0.75rem' : 'inherit' }}
                        >
                            (Optional)
                        </Typography>
                    </Typography>
                    <TextField
                        fullWidth
                        type="date"
                        size={shouldBeCompact ? "small" : "small"}
                        disabled={disabled}
                        value={displayDueDate || ''}
                        onChange={(e) => handleDueDateChange(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <CalendarTodayIcon fontSize={shouldBeCompact ? "small" : "small"} />
                                </InputAdornment>
                            ),
                            sx: {
                                fontSize: isMobile ? '0.875rem' : 'inherit'
                            }
                        }}
                        {...(formFieldNames.dueDate && register?.(formFieldNames.dueDate))}
                    />
                </Box>
            )}

            {/* Invoice Link Section - Only shown when isInvoiceLink is true */}
            {isInvoiceLink && (
                <Box sx={{
                    width: '100%',
                    mb: spacing
                }}>
                    <Typography
                        variant={shouldBeCompact ? "body2" : "body1"}
                        fontWeight="medium"
                        sx={{
                            mb: 0.5,
                            fontSize: isMobile ? '0.875rem' : 'inherit'
                        }}
                    >
                        Link to {isPurchase ? 'Purchase Invoice' : 'Invoice'}
                        <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                            sx={{ ml: 0.5, fontSize: isMobile ? '0.75rem' : 'inherit' }}
                        >
                            (Optional - Select an invoice to link)
                        </Typography>
                    </Typography>

                    {!shouldFetchInvoices() ? (
                        <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
                            {isPurchase
                                ? "Select a party or vendor first to see their purchase invoices"
                                : "Select a party first to see their invoices"}
                        </Alert>
                    ) : (
                        <Autocomplete
                            options={invoices}
                            loading={loadingInvoices}
                            value={selectedInvoice}
                            onChange={(_, newValue) => handleInvoiceSelect(newValue)}
                            inputValue={invoiceSearch}
                            onInputChange={(_, newInputValue) => setInvoiceSearch(newInputValue)}
                            getOptionLabel={(option) => {
                                // Safely get the number
                                const number = option.invoiceNumber || option.purchaseNumber || 'N/A';

                                // Safely format the date
                                let formattedDate = 'No Date';
                                const dateStr = option.invoiceDate || option.purchaseDate;
                                if (dateStr) {
                                    try {
                                        formattedDate = new Date(dateStr).toLocaleDateString();
                                    } catch {
                                        formattedDate = 'Invalid Date';
                                    }
                                }

                                return `${number} - ${formattedDate} - ₹${(option.totalAmount || 0).toFixed(2)}`;
                            }}
                            renderOption={(props, option) => {
                                // Safely get the number
                                const number = option.invoiceNumber || option.purchaseNumber || 'N/A';

                                // Safely format the date
                                let formattedDate = 'No Date';
                                const dateStr = option.invoiceDate || option.purchaseDate;
                                if (dateStr) {
                                    try {
                                        formattedDate = new Date(dateStr).toLocaleDateString();
                                    } catch {
                                        formattedDate = 'Invalid Date';
                                    }
                                }

                                return (
                                    <Box component="li" {...props}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                                <Typography variant="body2" fontWeight="medium">
                                                    {number}
                                                </Typography>
                                                {(option.status && option.gstType) && (
                                                    <Typography variant="caption" color={
                                                        option.status === 'PAID' ? 'success.main' :
                                                            option.status === 'PARTIAL_PAID' ? 'warning.main' :
                                                                'error.main'
                                                    }>
                                                        &nbsp; <Chip label={`${option.gstType}`} variant="outlined" />
                                                    </Typography>
                                                )}
                                            </Box>

                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    {formattedDate}
                                                </Typography>
                                                <Typography variant="caption" fontWeight="normal">
                                                    Total: <b> ₹{(option.totalAmount || 0).toFixed(2)}</b> &nbsp; / &nbsp;
                                                    Balance: <b>₹{(option.balanceAmount || 0).toFixed(2)}</b>
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                );
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    size={shouldBeCompact ? "small" : "small"}
                                    placeholder={getInvoiceLinkPlaceholder()}
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <>
                                                <InputAdornment position="start">
                                                    <DescriptionIcon fontSize={shouldBeCompact ? "small" : "small"} />
                                                </InputAdornment>
                                                {params.InputProps.startAdornment}
                                            </>
                                        ),
                                        endAdornment: (
                                            <>
                                                {loadingInvoices ? <CircularProgress color="inherit" size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                        sx: {
                                            fontSize: isMobile ? '0.875rem' : 'inherit'
                                        }
                                    }}
                                />
                            )}
                            fullWidth
                            disabled={disabled || !shouldFetchInvoices() || loadingInvoices}
                        />
                    )}

                    {selectedInvoice && (
                        <Alert severity="success" sx={{ mt: 1, fontSize: '0.875rem' }}>
                            {isPurchase ? 'Purchase invoice' : 'Invoice'} {selectedInvoice.invoiceNumber || selectedInvoice.purchaseNumber} selected. Invoice ID will be linked.
                        </Alert>
                    )}
                </Box>
            )}
        </Card>
    );
};

export default InvoiceDatesSection;